import React, { useState, useEffect } from 'react';
import './WeatherWidget.css';

// Направления ветра из wttr.in (16 румбов) в русские сокращения
const WIND_DIRECTIONS = {
  N: 'С', NNE: 'ССВ', NE: 'СВ', ENE: 'ВСВ',
  E: 'В', ESE: 'ВЮВ', SE: 'ЮВ', SSE: 'ЮЮВ',
  S: 'Ю', SSW: 'ЮЮЗ', SW: 'ЮЗ', WSW: 'ЗЮЗ',
  W: 'З', WNW: 'ЗСЗ', NW: 'СЗ', NNW: 'ССЗ',
};

// wttr.in переводит не все состояния (например, код 176 приходит по-английски
// даже с lang=ru), поэтому держим свой словарь как подстраховку.
const CONDITIONS_RU = {
  113: 'Ясно', 116: 'Переменная облачность', 119: 'Облачно', 122: 'Пасмурно',
  143: 'Дымка', 176: 'Местами дождь', 179: 'Местами снег',
  182: 'Местами дождь со снегом', 185: 'Местами изморось', 200: 'Гроза',
  227: 'Метель', 230: 'Пурга', 248: 'Туман', 260: 'Ледяной туман',
  263: 'Местами слабая морось', 266: 'Слабая морось', 281: 'Изморось',
  284: 'Сильная изморось', 293: 'Местами небольшой дождь', 296: 'Небольшой дождь',
  299: 'Местами умеренный дождь', 302: 'Умеренный дождь',
  305: 'Местами сильный дождь', 308: 'Сильный дождь', 311: 'Слабый ледяной дождь',
  314: 'Умеренный ледяной дождь', 317: 'Слабый дождь со снегом',
  320: 'Умеренный дождь со снегом', 323: 'Местами небольшой снег',
  326: 'Небольшой снег', 329: 'Местами умеренный снег', 332: 'Умеренный снег',
  335: 'Местами сильный снег', 338: 'Сильный снег', 350: 'Ледяная крупа',
  353: 'Небольшой ливень', 356: 'Умеренный ливень', 359: 'Сильный ливень',
  362: 'Слабый ливневый дождь со снегом', 365: 'Умеренный ливневый дождь со снегом',
  368: 'Небольшой снегопад', 371: 'Сильный снегопад', 374: 'Слабый ледяной дождь',
  377: 'Умеренный ледяной дождь', 386: 'Местами гроза с дождём',
  389: 'Гроза с дождём', 392: 'Местами гроза со снегом', 395: 'Гроза со снегом',
};

const CYRILLIC = /[а-яё]/i;

// Берём русское описание от API, если оно действительно на русском
function describeRu(entry, code) {
  const fromApi = entry.lang_ru && entry.lang_ru[0] && entry.lang_ru[0].value;
  if (fromApi && CYRILLIC.test(fromApi)) return fromApi.trim();
  if (CONDITIONS_RU[code]) return CONDITIONS_RU[code];
  const fallback = entry.weatherDesc && entry.weatherDesc[0] && entry.weatherDesc[0].value;
  return (fallback || '').trim();
}

// "05:46 AM" / "07:08 PM" -> "05:46" / "19:08"
function to24h(value) {
  if (!value) return null;
  const m = String(value).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return String(value);
  let hours = parseInt(m[1], 10);
  const suffix = (m[3] || '').toUpperCase();
  if (suffix === 'PM' && hours !== 12) hours += 12;
  if (suffix === 'AM' && hours === 12) hours = 0;
  return `${String(hours).padStart(2, '0')}:${m[2]}`;
}

function uvLabel(index) {
  if (index <= 2) return 'низкий';
  if (index <= 5) return 'умеренный';
  if (index <= 7) return 'высокий';
  if (index <= 10) return 'очень высокий';
  return 'экстремальный';
}

function WeatherIcon({ icon, hasClouds }) {
  if (icon === 'sunny') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="5"></circle>
        <line x1="12" y1="1" x2="12" y2="3"></line>
        <line x1="12" y1="21" x2="12" y2="23"></line>
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
        <line x1="1" y1="12" x2="3" y2="12"></line>
        <line x1="21" y1="12" x2="23" y2="12"></line>
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
      </svg>
    );
  }
  if (icon === 'cloudy') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
      </svg>
    );
  }
  if (icon === 'rain') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        {hasClouds && (
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
        )}
        <line x1="8" y1="20" x2="8" y2="23"></line>
        <line x1="12" y1="20" x2="12" y2="23"></line>
        <line x1="16" y1="20" x2="16" y2="23"></line>
        {!hasClouds && (
          <circle cx="12" cy="8" r="3" opacity="0.3"></circle>
        )}
      </svg>
    );
  }
  if (icon === 'snow') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        {hasClouds && (
          <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
        )}
        <path d="M8 20l1 1M12 20l1 1M16 20l1 1M9 21l1-1M13 21l1-1M17 21l1-1"></path>
        <circle cx="8" cy="20" r="0.5" fill="currentColor"></circle>
        <circle cx="12" cy="20" r="0.5" fill="currentColor"></circle>
        <circle cx="16" cy="20" r="0.5" fill="currentColor"></circle>
        {!hasClouds && (
          <circle cx="12" cy="8" r="3" opacity="0.3"></circle>
        )}
      </svg>
    );
  }
  return null;
}

export default function WeatherWidget() {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadWeather();
    // Погода в подробном окне не должна «застревать» на утренних значениях
    // у тех, кто держит вкладку открытой весь день.
    const timer = setInterval(loadWeather, 30 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const loadWeather = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Используем wttr.in API (бесплатный, без ключа)
      // Южное Бутово, Москва
      const response = await fetch('https://wttr.in/Южное+Бутово?format=j1&lang=ru', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Не удалось загрузить погоду');
      }
      
      const data = await response.json();
      
      // Извлекаем текущую погоду
      const current = data.current_condition[0];
      const condition = current.weatherDesc[0].value.toLowerCase();
      const temp = parseInt(current.temp_C);
      const code = parseInt(current.weatherCode);
      
      const iconType = getWeatherIcon(code, condition);
      const hasClouds = code >= 116 && code <= 122 || 
                       condition.toLowerCase().includes('облачно') || 
                       condition.toLowerCase().includes('cloudy') ||
                       condition.toLowerCase().includes('пасмурно');
      
      setWeather({
        temp,
        condition,
        code,
        icon: iconType,
        hasClouds: hasClouds && iconType !== 'sunny',
        details: buildDetails(data, current, iconType),
      });
    } catch (err) {
      console.error('Error loading weather:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Готовим данные для подробного окна: всё берётся из того же ответа API
  const buildDetails = (data, current, iconType) => {
    const today = (data.weather && data.weather[0]) || {};
    const astronomy = (today.astronomy && today.astronomy[0]) || {};

    const windMs = Math.round(parseInt(current.windspeedKmph, 10) / 3.6);
    const windDir = WIND_DIRECTIONS[current.winddir16Point] || current.winddir16Point || '';
    // wttr.in отдаёт давление в гПа, привычнее миллиметры ртутного столба
    const pressureMm = Math.round(parseInt(current.pressure, 10) * 0.750062);
    const uv = parseInt(current.uvIndex, 10) || 0;

    const description = describeRu(current, parseInt(current.weatherCode, 10));

    // Почасовой прогноз на оставшуюся часть дня (шаг 3 часа)
    const nowHour = new Date().getHours();
    const allHours = (today.hourly || []).map((h) => ({
      hour: Math.floor(parseInt(h.time, 10) / 100),
      temp: parseInt(h.tempC, 10),
      rain: parseInt(h.chanceofrain, 10),
      icon: getWeatherIcon(parseInt(h.weatherCode, 10), (h.weatherDesc && h.weatherDesc[0].value) || ''),
    }));
    let hours = allHours.filter((h) => h.hour >= nowHour).slice(0, 5);
    // Поздним вечером впереди почти ничего не осталось — показываем хвост дня
    if (hours.length < 3) hours = allHours.slice(-3);

    return {
      description: description.charAt(0).toUpperCase() + description.slice(1),
      icon: iconType,
      temp: parseInt(current.temp_C, 10),
      feelsLike: parseInt(current.FeelsLikeC, 10),
      maxTemp: today.maxtempC != null ? parseInt(today.maxtempC, 10) : null,
      minTemp: today.mintempC != null ? parseInt(today.mintempC, 10) : null,
      humidity: parseInt(current.humidity, 10),
      windMs,
      windDir,
      pressureMm,
      cloudcover: parseInt(current.cloudcover, 10),
      precipMM: parseFloat(current.precipMM),
      visibility: parseInt(current.visibility, 10),
      uv,
      uvText: uvLabel(uv),
      sunrise: to24h(astronomy.sunrise),
      sunset: to24h(astronomy.sunset),
      observedAt: to24h(current.observation_time),
      hours,
    };
  };

  const getWeatherIcon = (code, condition) => {
    const cond = condition.toLowerCase();
    
    // Проверяем снег (приоритет выше дождя)
    if (cond.includes('снег') || cond.includes('snow') || 
        code === 227 || code === 230 || 
        (code >= 260 && code <= 284) ||
        (code >= 350 && code <= 377) ||
        (code >= 386 && code <= 389)) {
      return 'snow';
    }
    
    // Проверяем дождь
    if (cond.includes('дождь') || cond.includes('rain') || 
        cond.includes('ливень') || cond.includes('shower') ||
        (code >= 176 && code <= 185) || 
        (code >= 200 && code <= 202) ||
        (code >= 263 && code <= 284) || 
        (code >= 293 && code <= 302) ||
        (code >= 305 && code <= 320) || 
        (code >= 323 && code <= 338)) {
      return 'rain';
    }
    
    // Ясная погода
    if (code === 113 || cond.includes('ясно') || cond.includes('clear')) {
      return 'sunny';
    }
    
    // Облачно
    if (code >= 116 && code <= 122 || 
        cond.includes('облачно') || cond.includes('cloudy') ||
        cond.includes('пасмурно') || cond.includes('overcast')) {
      return 'cloudy';
    }
    
    return 'cloudy'; // По умолчанию
  };

  if (loading && !weather) {
    return (
      <div className="weather-widget">
        <div className="weather-icon weather-loading">
          <div className="weather-spinner"></div>
        </div>
      </div>
    );
  }

  if (error || !weather) {
    return null; // Не показываем виджет при ошибке
  }

  const d = weather.details;

  return (
    <div
      className="weather-widget"
      onMouseEnter={() => setShowDetails(true)}
      onMouseLeave={() => setShowDetails(false)}
    >
      <div className="weather-icon">
        <WeatherIcon icon={weather.icon} hasClouds={weather.hasClouds} />
      </div>
      <span className="weather-temp">{weather.temp}°</span>

      {showDetails && d && (
        <div className="weather-popup">
          <div className="weather-popup-header">
            <div className="weather-popup-icon">
              <WeatherIcon icon={weather.icon} hasClouds={weather.hasClouds} />
            </div>
            <div className="weather-popup-headline">
              <span className="weather-popup-temp">{d.temp}°</span>
              <span className="weather-popup-desc">{d.description}</span>
            </div>
          </div>

          <div className="weather-popup-summary">
            Ощущается как {d.feelsLike}°
            {d.maxTemp !== null && d.minTemp !== null && (
              <> · днём {d.maxTemp}° / ночью {d.minTemp}°</>
            )}
          </div>

          <div className="weather-popup-grid">
            <div className="weather-popup-row">
              <span className="weather-popup-label">Ветер</span>
              <span className="weather-popup-value">
                {d.windMs} м/с{d.windDir ? `, ${d.windDir}` : ''}
              </span>
            </div>
            <div className="weather-popup-row">
              <span className="weather-popup-label">Влажность</span>
              <span className="weather-popup-value">{d.humidity}%</span>
            </div>
            <div className="weather-popup-row">
              <span className="weather-popup-label">Давление</span>
              <span className="weather-popup-value">{d.pressureMm} мм рт. ст.</span>
            </div>
            <div className="weather-popup-row">
              <span className="weather-popup-label">Облачность</span>
              <span className="weather-popup-value">{d.cloudcover}%</span>
            </div>
            <div className="weather-popup-row">
              <span className="weather-popup-label">Осадки</span>
              <span className="weather-popup-value">{d.precipMM} мм</span>
            </div>
            <div className="weather-popup-row">
              <span className="weather-popup-label">УФ-индекс</span>
              <span className="weather-popup-value">{d.uv} ({d.uvText})</span>
            </div>
            <div className="weather-popup-row">
              <span className="weather-popup-label">Видимость</span>
              <span className="weather-popup-value">{d.visibility} км</span>
            </div>
          </div>

          {(d.sunrise || d.sunset) && (
            <div className="weather-popup-sun">
              {d.sunrise && (
                <span className="weather-popup-sun-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 18a5 5 0 0 0-10 0"></path>
                    <line x1="12" y1="2" x2="12" y2="9"></line>
                    <line x1="4.22" y1="10.22" x2="5.64" y2="11.64"></line>
                    <line x1="1" y1="18" x2="3" y2="18"></line>
                    <line x1="21" y1="18" x2="23" y2="18"></line>
                    <line x1="18.36" y1="11.64" x2="19.78" y2="10.22"></line>
                    <line x1="23" y1="22" x2="1" y2="22"></line>
                    <polyline points="8 6 12 2 16 6"></polyline>
                  </svg>
                  Восход {d.sunrise}
                </span>
              )}
              {d.sunset && (
                <span className="weather-popup-sun-item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 18a5 5 0 0 0-10 0"></path>
                    <line x1="12" y1="9" x2="12" y2="2"></line>
                    <line x1="4.22" y1="10.22" x2="5.64" y2="11.64"></line>
                    <line x1="1" y1="18" x2="3" y2="18"></line>
                    <line x1="21" y1="18" x2="23" y2="18"></line>
                    <line x1="18.36" y1="11.64" x2="19.78" y2="10.22"></line>
                    <line x1="23" y1="22" x2="1" y2="22"></line>
                    <polyline points="16 5 12 9 8 5"></polyline>
                  </svg>
                  Закат {d.sunset}
                </span>
              )}
            </div>
          )}

          {d.hours.length > 0 && (
            <div className="weather-popup-hourly">
              {d.hours.map((h) => (
                <div key={h.hour} className="weather-hour">
                  <span className="weather-hour-time">
                    {String(h.hour).padStart(2, '0')}:00
                  </span>
                  <span className="weather-hour-icon">
                    <WeatherIcon icon={h.icon} hasClouds={h.icon !== 'sunny'} />
                  </span>
                  <span className="weather-hour-temp">{h.temp}°</span>
                  <span className="weather-hour-rain">{h.rain}%</span>
                </div>
              ))}
            </div>
          )}

          {d.observedAt && (
            <div className="weather-popup-footer">
              Южное Бутово · данные на {d.observedAt}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
